import { Entity, LoadingState } from '@zdr-tools/zdr-entities';
/** Which screen of the login site shows. */
export var SignInStep;
(function (SignInStep) {
    SignInStep["EMAIL"] = "EMAIL";
    SignInStep["CODE"] = "CODE";
    /** The code was accepted; the code step stays locked until the page leaves. */
    SignInStep["SIGNED_IN"] = "SIGNED_IN";
})(SignInStep || (SignInStep = {}));
/** Which request is in flight. */
export var SignInActivity;
(function (SignInActivity) {
    SignInActivity["NONE"] = "NONE";
    SignInActivity["SENDING_CODE"] = "SENDING_CODE";
    SignInActivity["CHECKING_CODE"] = "CHECKING_CODE";
    SignInActivity["RESENDING_CODE"] = "RESENDING_CODE";
})(SignInActivity || (SignInActivity = {}));
/** The last refusal or failure. */
export var SignInFailure;
(function (SignInFailure) {
    SignInFailure["NONE"] = "NONE";
    SignInFailure["INVALID_EMAIL"] = "INVALID_EMAIL";
    SignInFailure["TOO_MANY_CODES"] = "TOO_MANY_CODES";
    SignInFailure["WRONG_CODE"] = "WRONG_CODE";
    SignInFailure["CODE_EXPIRED"] = "CODE_EXPIRED";
    SignInFailure["TOO_MANY_ATTEMPTS"] = "TOO_MANY_ATTEMPTS";
    SignInFailure["UNAVAILABLE"] = "UNAVAILABLE";
})(SignInFailure || (SignInFailure = {}));
/** A success banner on the code step. */
export var SignInNotice;
(function (SignInNotice) {
    SignInNotice["NONE"] = "NONE";
    SignInNotice["NEW_CODE_SENT"] = "NEW_CODE_SENT";
})(SignInNotice || (SignInNotice = {}));
const FEWEST_ATTEMPTS_LEFT_AFTER_WRONG_CODE = 1;
const MOST_ATTEMPTS_LEFT_AFTER_WRONG_CODE = 4;
/** The state of one email-code sign-in on the login site. */
export class SignIn extends Entity {
    step;
    email;
    activity;
    loadingState;
    failure;
    attemptsLeft;
    notice;
    resendAllowedAt;
    secondsUntilResend;
    isCodeUsable;
    isBusy;
    canRequestNewCode;
    failureRevision;
    constructor(initializer) {
        super('sign-in');
        this.step = this.createPropEventBroker('step', initializer.step);
        this.email = this.createPropEventBroker('email', initializer.email);
        this.activity = this.createPropEventBroker('activity', initializer.activity);
        this.loadingState = this.createPropEventBroker('loadingState', initializer.loadingState);
        this.failure = this.createPropEventBroker('failure', initializer.failure);
        this.attemptsLeft = this.createPropEventBroker('attemptsLeft', initializer.attemptsLeft);
        this.notice = this.createPropEventBroker('notice', initializer.notice);
        this.resendAllowedAt = this.createPropEventBroker('resendAllowedAt', initializer.resendAllowedAt);
        this.secondsUntilResend = this.createPropEventBroker('secondsUntilResend', initializer.secondsUntilResend);
        this.isCodeUsable = this.createPropEventBroker('isCodeUsable', initializer.isCodeUsable);
        this.failureRevision = this.createPropEventBroker('failureRevision', initializer.failureRevision);
        this.isBusy = this.createPropEventBroker('isBusy', this.findIsBusy());
        this.canRequestNewCode = this.createPropEventBroker('canRequestNewCode', this.findCanRequestNewCode());
    }
    getEntityName() {
        return 'SignIn';
    }
    isIdle() {
        return !this.isBusy.get();
    }
    isIdleOnCodeStep() {
        return this.isIdle() && this.step.get() === SignInStep.CODE;
    }
    markSendingCode(email) {
        this.email.set(email.trim());
        this.startRequest(SignInActivity.SENDING_CODE);
    }
    markCodeSent(resendAllowedAt) {
        this.step.set(SignInStep.CODE);
        this.resendAllowedAt.set(resendAllowedAt);
        this.isCodeUsable.set(true);
        this.endRequest(LoadingState.DONE);
    }
    markResendingCode() {
        this.assertOnCodeStep('markResendingCode');
        this.startRequest(SignInActivity.RESENDING_CODE);
    }
    markNewCodeSent(resendAllowedAt) {
        this.failure.set(SignInFailure.NONE);
        this.notice.set(SignInNotice.NEW_CODE_SENT);
        this.resendAllowedAt.set(resendAllowedAt);
        this.isCodeUsable.set(true);
        this.endRequest(LoadingState.DONE);
    }
    markCheckingCode() {
        this.assertOnCodeStep('markCheckingCode');
        this.startRequest(SignInActivity.CHECKING_CODE);
    }
    markSignedIn() {
        this.step.set(SignInStep.SIGNED_IN);
        this.loadingState.set(LoadingState.DONE);
        this.syncDerivedState();
    }
    // No request was made, so the activity is left as it is.
    markInvalidEmail() {
        this.notice.set(SignInNotice.NONE);
        this.recordFailure(SignInFailure.INVALID_EMAIL);
        this.loadingState.set(LoadingState.ERROR);
        this.syncDerivedState();
    }
    markTooManyCodes(resendAllowedAt) {
        this.resendAllowedAt.set(resendAllowedAt);
        this.endRequestWithFailure(SignInFailure.TOO_MANY_CODES);
    }
    markWrongCode(attemptsLeft, resendAllowedAt) {
        if (!Number.isInteger(attemptsLeft) || attemptsLeft < FEWEST_ATTEMPTS_LEFT_AFTER_WRONG_CODE || attemptsLeft > MOST_ATTEMPTS_LEFT_AFTER_WRONG_CODE) {
            throw new Error(`A wrong code leaves 1 to 4 attempts, not ${attemptsLeft}`);
        }
        this.attemptsLeft.set(attemptsLeft);
        this.resendAllowedAt.set(resendAllowedAt);
        this.endRequestWithFailure(SignInFailure.WRONG_CODE);
    }
    markCodeExpired(resendAllowedAt) {
        this.resendAllowedAt.set(resendAllowedAt);
        this.isCodeUsable.set(false);
        this.endRequestWithFailure(SignInFailure.CODE_EXPIRED);
    }
    markTooManyAttempts(resendAllowedAt) {
        this.resendAllowedAt.set(resendAllowedAt);
        this.isCodeUsable.set(false);
        this.endRequestWithFailure(SignInFailure.TOO_MANY_ATTEMPTS);
    }
    markUnavailable() {
        this.endRequestWithFailure(SignInFailure.UNAVAILABLE);
    }
    returnToEmailStep() {
        if (!this.isIdleOnCodeStep()) {
            throw new Error('returnToEmailStep is allowed only on the code step with no request in flight');
        }
        this.failure.set(SignInFailure.NONE);
        this.notice.set(SignInNotice.NONE);
        this.step.set(SignInStep.EMAIL);
        this.syncDerivedState();
    }
    updateSecondsUntilResend(seconds) {
        this.secondsUntilResend.set(Math.max(0, seconds));
        this.syncDerivedState();
    }
    assertOnCodeStep(methodName) {
        if (this.step.get() !== SignInStep.CODE) {
            throw new Error(`${methodName} is allowed only on the code step`);
        }
    }
    startRequest(activity) {
        this.failure.set(SignInFailure.NONE);
        this.notice.set(SignInNotice.NONE);
        this.activity.set(activity);
        this.loadingState.set(LoadingState.LOADING);
        this.syncDerivedState();
    }
    endRequest(loadingState) {
        this.activity.set(SignInActivity.NONE);
        this.loadingState.set(loadingState);
        this.syncDerivedState();
    }
    endRequestWithFailure(failure) {
        this.recordFailure(failure);
        this.endRequest(LoadingState.ERROR);
    }
    recordFailure(failure) {
        this.failure.set(failure);
        this.failureRevision.set(this.failureRevision.get() + 1);
    }
    syncDerivedState() {
        this.isBusy.set(this.findIsBusy());
        this.canRequestNewCode.set(this.findCanRequestNewCode());
    }
    findIsBusy() {
        return this.activity.get() !== SignInActivity.NONE || this.step.get() === SignInStep.SIGNED_IN;
    }
    findCanRequestNewCode() {
        return this.step.get() === SignInStep.CODE && !this.findIsBusy() && this.secondsUntilResend.get() === 0;
    }
}
//# sourceMappingURL=SignIn.js.map